import mapboxgl from 'mapbox-gl'
import { useEffect, useState, useRef } from 'react'

import { AssetBox } from '@/components/custom/assetbox';
import { getLayer } from '@/actions/maps/getlayer';
import { SearchBar } from '@/components/custom/searchbar';
import { AssetPopup } from '@/components/custom/assetpopup';

import { assetInterface } from '@/interfaces'

import './index.css'


interface OverviewProps extends React.HTMLAttributes<HTMLElement> {
    navbarOpen: boolean
}

const marker = new mapboxgl.Marker({
    color: "#1c2c50"
})

const Overview = ({navbarOpen, ...props}: OverviewProps) => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const [maplon, setLng] = useState(20.9);
    const [maplat, setLat] = useState(42.35);
    const [zoom, setZoom] = useState(2);
    const [loading, setLoading] = useState<boolean>(false)
    const [focusedOnAsset, setFocusedOnAsset] = useState<boolean>(false)
    const [assetInFocus, setAssetInFocus] = useState({} as assetInterface)


    let accessToken: string | undefined = process.env.REACT_APP_MAPBOX_APIKEY

    if(!accessToken){
        console.error("Mapbox API KEY is not set")
        accessToken = ""
    } else {
        mapboxgl.accessToken = accessToken
    }

    const load_data = async (current_map: mapboxgl.Map) => {

        //TODO: make tile ID env variable
        const tileLink ='https://api.mapbox.com/v4/max-rijnierse.1o2fsagp/{z}/{x}/{y}.mvt?access_token=' + accessToken

        //loading directly from mapbox
        await current_map.addSource('tileSource', {
                'type': 'vector',
                'url': 'mapbox://max-rijnierse.263esgnm'
            })

        //TODO: make more efficient: load less tiles
        //TODO: make interpolated heatmap layer from point data instead of grid?
        await current_map.addLayer({
            'id': 'assets',
            'type': 'fill',
            'source': 'tileSource',
            'source-layer': 'dense_map',
            'minzoom': 0,
            'maxzoom': 14,
            'paint': {
                'fill-color': [
                    'interpolate',
                    ['linear'],
                    ['get', 'value'],
                    // 0, '#272828',
                    0, '#595c49',
                    20, '#F8C471',
                    30, '#dc7633',
                    50, '#d35400',
                    70, '#C0392B',
                    ],
                'fill-opacity' : 0.7,
                'fill-outline-color': 'transparent'
            }
        })

        setLoading(false)

        // loading from local geojson file instead:
        // getLayer('generic_map').then(async (res) => { 
        //     await current_map.addSource('asset_data', {
        //            type: 'geojson',
        //            data: res
        //         })
            

        //     setLoading(false)
        //     current_map.addLayer({
        //         'id': 'assets',
        //         'type': 'fill',
        //         'source': 'asset_data',
        //         'paint': {
        //             'fill-color': [
        //                 'interpolate',
        //                 ['linear'],
        //                 ['get', 'value'],
        //                 0, '#2980B9',
        //                 5, '#5499C7',
        //                 10, '#7FB3D5',
        //                 15, '#A9CCE3',
        //                 20, '#D4E6F1',
        //                 // turns to yellow / orange (warning)
        //                 25, '#FEF9E7',
        //                 30, '#FDEBD0',
        //                 35, '#FAD7A0',
        //                 40, '#F8C471',
        //                 45, '#F5B041',  
        //                 // turns red (high risk)
        //                 50, '#E6B0AA',
        //                 60, '#D98880',
        //                 70, '#CD6155',
        //                 80, '#C0392B',
        //                 ],
        //             'fill-opacity' : 0.7,
        //             'fill-outline-color': 'transparent'
        //         }
        //     })
        // })
    }

    useEffect(() => {
        // initialize map only once & only when a container is available
        if (!map.current && mapContainer){ 
            map.current = new mapboxgl.Map({
                container: mapContainer.current || "",
                style: 'mapbox://styles/mapbox/dark-v11',
                center: [maplon, maplat],
                zoom: zoom
            });   

            if(map.current){
                map.current.on('load', () => {
                    setLoading(true)
                    load_data(map.current as mapboxgl.Map)
                
                })
            }
        }else if (map.current){
            //map.current exists
            map.current.resize();
        }       
    }, [navbarOpen]); // triggers upon first render and when navbaropen changes

   
    // let mapMarkers = []

    useEffect(() => {
        if(map.current){
            marker.remove()

            if(focusedOnAsset){
                
                if(assetInFocus.country !== undefined){
                    console.log('asset is in focus')
                    console.log(assetInFocus)

                    map.current.flyTo({
                        center: [parseFloat(assetInFocus.lon), parseFloat(assetInFocus.lat)],
                        zoom: 12,
                        pitch: 70,
                        essential: true
                    })

                    marker.setLngLat([parseFloat(assetInFocus.lon), parseFloat(assetInFocus.lat)])
                        .addTo(map.current)
                  

                }
            } else {
                // focusedOnAsset is false, remove pitch
                map.current.flyTo({
                    center: [maplon,maplat],
                    zoom: zoom,
                    pitch: 0,
                    essential: true
                })
            }
        }
    }, [focusedOnAsset, assetInFocus])

    return (
        <div className="w-full h-full flex flex-row" >
            <div className='w-1/4 min-w-[150px] lg:w-1/5 hidden md:block'>
                <AssetBox focusedOnAsset={focusedOnAsset} setFocusedOnAsset={setFocusedOnAsset} assetInFocus={assetInFocus} setAssetInFocus={setAssetInFocus} />  
            </div>
            <div className='relative h-full grow'> 
                <div ref={mapContainer} className="map-container h-full w-full">
                </div>
                <div className='z-20 absolute top-0 left-0 ml-10 mt-7 w-1/2 md:w-1/3 lg:w-1/4 lg:ml-15 min-w-max'>
                    {/* SearchBar loaded in from jsx component file as the library was in JS */}
                    <SearchBar map={map} accessToken={accessToken} setAssetInFocus={setAssetInFocus} assetInFocus={assetInFocus} setFocusedOnAsset={setFocusedOnAsset}/>
                </div> 
                {loading ? 
                    <div className="flex flex-row absolute top-0 right-0 m-5 p-3 w-max opacity-80 bg-secondary" role="status">
                        <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                        </svg>
                        <p className='text-sm pt-2 pl-5'>Loading Data...</p>
                    </div>
                : null}
                {focusedOnAsset ? 
                    <div className='z-20 absolute bottom-0 right-0 mr-10 mb-10 lg:mr-15 xl:mr-28 xl:mb-28'>
                        <AssetPopup setFocusedOnAsset={setFocusedOnAsset} assetInFocus={assetInFocus}/>     
                    </div>
                : null}
            </div>
        </div>
    )
}

export default Overview